import { useEffect, useState } from 'react'
import {
  CalendarDays,
  ChevronDown,
  Clock3,
  Headphones,
  Info,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  RotateCcw,
  Settings,
  ShoppingBag,
  Store,
  Truck,
  Wallet,
  X,
} from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import stylereLogo from '@/assets/stylere-logo.png'
import { useAuth } from '@/hooks/use-auth'
import { ROUTES } from '@/lib/navigation'
import { isReturnPath } from '@/lib/returnFlow'

const getPublicNavigation = (isMember: boolean) => [
  { label: 'How It Works', description: 'Pickup, delivery, and tracking', to: isMember ? '/member/how-it-works' : '/how-it-works', match: ['/how-it-works', '/member/how-it-works'], icon: Info },
  { label: 'Stores', description: 'Browse supported local stores', to: isMember ? '/member/stores' : ROUTES.STORE_SELECTION, match: [ROUTES.STORE_SELECTION, '/member/stores', '/stores'], icon: Store },
  { label: 'For Retailers', description: 'Partner delivery tools', to: '/retailer-onboarding', match: ['/retailer-onboarding'], icon: Truck },
  { label: 'Resources', description: 'Help, services, and delivery info', to: isMember ? '/member/help-center' : ROUTES.HELP_CENTER, match: [ROUTES.HELP_CENTER, '/platform-services', '/delivery-info', '/member/help-center', '/member/help'], icon: Headphones },
  { label: 'About', description: 'Company and team', to: '/about-us', match: ['/about-us', '/team'], icon: Info },
]

export default function PublicHeader() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const inReturns = isReturnPath(location.pathname)
  const [menuOpen, setMenuOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)

  useEffect(() => {
    setMenuOpen(false)
    setAccountOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!menuOpen && !accountOpen) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        setAccountOpen(false)
      }
    }

    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [menuOpen, accountOpen])

  const initials = user
    ? `${user.firstName?.[0] || user.username?.[0] || user.email[0]}${user.lastName?.[0] || ''}`.toUpperCase()
    : ''
  const navLinks = getPublicNavigation(Boolean(user))
  const memberMenuItems = [
    { label: 'Membership Dashboard', to: '/membership/dashboard', icon: LayoutDashboard },
    { label: 'Wallet', to: ROUTES.WALLET, icon: Wallet },
    { label: 'Account Settings', to: ROUTES.SETTINGS, icon: Settings },
    { label: 'Curbside Pickup', to: '/member/order/store-details', icon: CalendarDays },
    { label: 'Returns', to: ROUTES.MEMBER_RETURNS_STORE_DETAILS, icon: RotateCcw },
    { label: 'Pickup History', to: ROUTES.PICKUP_HISTORY, icon: Clock3 },
  ]

  const handleLogout = async () => {
    await logout()
    navigate(ROUTES.HOME)
  }

  return (
    <header data-site-header className="public-header">
      <div className="public-header__inner">
        <Link to={ROUTES.HOME} className="public-header__logo-link" aria-label="Style.re home">
          <img src={stylereLogo} alt="Style.re" className="public-header__logo" />
        </Link>

        <nav className="public-header__desktop-nav" aria-label="Public navigation">
          {navLinks.map((item) => {
            const isActive = item.match.includes(location.pathname)
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`public-header__nav-link${isActive ? ' is-active' : ''}`}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>

        <div className="public-header__actions">
          <Link
            to={inReturns ? (user ? ROUTES.MEMBER_RETURNS_STORE_DETAILS : ROUTES.RETURNS_STORE_DETAILS) : user ? '/member/order/store-details' : ROUTES.STORE_DETAILS}
            className="public-button public-button--primary public-header__schedule"
          >
            {inReturns ? <RotateCcw aria-hidden="true" /> : <CalendarDays aria-hidden="true" />}
            {inReturns ? 'Schedule Return' : 'Schedule a Pickup'}
          </Link>
          <Link
            to={ROUTES.PICKUP_HISTORY}
            className="public-button public-button--secondary public-header__track"
          >
            <MapPin aria-hidden="true" />
            {inReturns ? 'Track Return' : 'Track Order'}
          </Link>
          {user && (
            <Link
              to="/membership/dashboard"
              className="public-button public-button--member public-header__member"
            >
              <LayoutDashboard aria-hidden="true" />
              Member Dashboard
            </Link>
          )}
          <div className="public-header__account-wrap">
            {user ? (
              <>
                <button
                  type="button"
                  className="public-header__account public-header__account--member"
                  aria-label="Open member menu"
                  aria-expanded={accountOpen}
                  aria-controls="public-account-menu"
                  onClick={() => setAccountOpen((open) => !open)}
                >
                  <span className="public-header__avatar" aria-hidden="true">{initials}</span>
                  <ChevronDown className="public-header__account-chevron" aria-hidden="true" />
                </button>
                <div
                  id="public-account-menu"
                  className={`public-header__account-menu${accountOpen ? ' is-open' : ''}`}
                  hidden={!accountOpen}
                >
                  <div className="public-header__account-address">
                    <MapPin aria-hidden="true" />
                    <span>{user.streetAddress || 'Set your address'}</span>
                  </div>
                  <div className="public-header__account-list">
                    {memberMenuItems.map(({ label, to, icon: Icon }) => (
                      <Link key={to} to={to} className="public-header__account-item">
                        <Icon aria-hidden="true" />
                        <span>{label}</span>
                      </Link>
                    ))}
                  </div>
                  <div className="public-header__account-email">{user.email}</div>
                  <button type="button" className="public-header__account-item public-header__logout" onClick={handleLogout}>
                    <LogOut aria-hidden="true" />
                    <span>Log out</span>
                  </button>
                </div>
              </>
            ) : (
              <>
              <Link to="/auth?tab=login" className="public-button public-button--ghost public-header__auth public-header__mobile-signin">
                Sign In
              </Link>
              <Link to="/auth?tab=register" className="public-button public-button--gold public-header__auth">
                Register
              </Link>
              </>
            )}
          </div>
          <button
            type="button"
            className="public-header__menu-button"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            aria-controls="public-mobile-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>

      <div
        id="public-mobile-navigation"
        className={`public-header__mobile-panel${menuOpen ? ' is-open' : ''}`}
        hidden={!menuOpen}
      >
        <nav className="public-header__mobile-nav" aria-label="Mobile public navigation">
          {navLinks.map((item) => {
            const isActive = item.match.includes(location.pathname)
            const Icon = item.icon
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`public-header__mobile-link${isActive ? ' is-active' : ''}`}
              >
                <span className="public-header__mobile-link-icon">
                  <Icon aria-hidden="true" />
                </span>
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
              </Link>
            )
          })}
        </nav>
        <div className="public-header__mobile-actions">
          {user && (
            <Link to="/membership/dashboard" className="public-button public-button--member">
              <LayoutDashboard aria-hidden="true" />
              Member Dashboard
            </Link>
          )}
          <Link to={inReturns ? (user ? ROUTES.MEMBER_RETURNS_STORE_DETAILS : ROUTES.RETURNS_STORE_DETAILS) : user ? '/member/order/store-details' : ROUTES.STORE_DETAILS} className="public-button public-button--primary">
            {inReturns ? <RotateCcw aria-hidden="true" /> : <CalendarDays aria-hidden="true" />}
            {inReturns ? 'Schedule Return' : 'Schedule a Pickup'}
          </Link>
          <Link to={ROUTES.PICKUP_HISTORY} className="public-button public-button--secondary">
            <ShoppingBag aria-hidden="true" />
            {inReturns ? 'Track Return' : 'Track Order'}
          </Link>
          {!user && (
            <>
              <Link to="/auth?tab=login" className="public-button public-button--ghost">
                Sign In
              </Link>
              <Link to="/auth?tab=register" className="public-button public-button--gold">
                Register
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
